import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, AlertTriangle, Calendar, DollarSign, Target, ChevronRight, ArrowRight, Briefcase, Globe, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BySorted = React.memo(({ data, limit }) => {
  const sorted = useMemo(() => Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, limit), [data, limit]);
  if (sorted.length === 0) return <p className="text-xs" style={{ color: '#8B7355' }}>No data yet</p>;
  return (
    <div className="flex flex-col gap-2 mb-5">
      {sorted.map(([name, count]) => (
        <div key={name} className="flex items-center justify-between">
          <span className="text-xs" style={{ color: '#4A3A2E' }}>{name}</span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#D4AF3720', color: '#B8860B' }}>{count}</span>
        </div>
      ))}
    </div>
  );
});

const ByCategorySorted = React.memo(({ data, limit }) => {
  const sorted = useMemo(() => Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, limit), [data, limit]);
  return (
    <div className="flex flex-col gap-2">
      {sorted.map(([cat, count]) => (
        <div key={cat} className="flex items-center justify-between">
          <span className="text-xs" style={{ color: '#4A3A2E' }}>{cat}</span>
          <span className="text-xs" style={{ color: '#8B7355' }}>{count}</span>
        </div>
      ))}
    </div>
  );
});

const BIDashboard = () => {
  const [data, setData] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/opportunities/dashboard`, { withCredentials: true }).catch(() => axios.get(`${API_URL}/api/opportunities/dashboard`)),
      axios.get(`${API_URL}/api/opportunities`, { withCredentials: true }).catch(() => axios.get(`${API_URL}/api/opportunities`)),
    ]).then(([dashRes, oppsRes]) => {
      setData(dashRes.data);
      setOpportunities(oppsRes.data || []);
    }).catch(() => {
      setData({ total: 0, closing_soon: [], high_value: [], red_priority: [], orange_priority: [], upcoming_30: 0, upcoming_90: 0, total_pipeline: 0, by_business: {}, by_category: {}, by_month: {} });
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF6' }}><div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} /></div>;

  const priorityColor = { red: '#DC2626', orange: '#F59E0B', green: '#10B981' };
  const priorityLabel = { red: 'Immediate Action', orange: 'Plan This Month', green: 'Long-Term' };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAF6', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="bi-dashboard">
      <nav className="bg-white border-b px-6 py-3" style={{ borderColor: '#E8E0D4' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 no-underline">
              <img src="/maluti-logo-web.png" alt="Maluti" className="h-7 object-contain" />
              <span className="text-xs font-medium" style={{ color: '#2C1A0E' }}>Ops Suite</span>
            </Link>
            <span className="text-xs" style={{ color: '#D1D5DB' }}>/</span>
            <span className="text-xs font-semibold" style={{ color: '#B8860B' }}>BID</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/opportunities" className="text-[10px] uppercase tracking-wider font-medium no-underline hover:underline" style={{ color: '#8B7355' }}>Opportunities</Link>
            <Link to="/annual-planner" className="text-[10px] uppercase tracking-wider font-medium no-underline hover:underline" style={{ color: '#8B7355' }}>Planner</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-light" style={{ color: '#2C1A0E' }}>Business Intelligence Dashboard</h1>
            <p className="text-xs mt-1" style={{ color: '#8B7355' }}>Opportunity pipeline, revenue roadmap & commercial planning</p>
          </div>
          <button onClick={() => navigate('/opportunities?new=1')} className="px-4 py-2 text-[10px] uppercase tracking-wider font-medium" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="add-opportunity-btn">
            + Add Opportunity
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Pipeline', value: `£${(data.total_pipeline || 0).toLocaleString()}`, icon: <DollarSign className="w-4 h-4" />, color: '#B8860B' },
            { label: 'Active Opportunities', value: data.total, icon: <Target className="w-4 h-4" />, color: '#2C1A0E' },
            { label: 'Next 30 Days', value: data.upcoming_30, icon: <Calendar className="w-4 h-4" />, color: '#0D9488' },
            { label: 'Next 90 Days', value: data.upcoming_90, icon: <TrendingUp className="w-4 h-4" />, color: '#6366F1' },
          ].map(({ label, value, icon, color }) => (
            <div key={label} className="bg-white p-4 border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
              <div className="flex items-center gap-2 mb-2" style={{ color }}>{icon}<span className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>{label}</span></div>
              <p className="text-2xl font-light" style={{ color: '#2C1A0E' }}>{value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Priority Alerts */}
          <div className="bg-white p-5 border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
            <h3 className="text-[10px] uppercase tracking-wider font-semibold mb-4" style={{ color: '#DC2626' }}>🔴 Immediate Action ({data.red_priority?.length || 0})</h3>
            <div className="flex flex-col gap-2">
              {(data.red_priority || []).slice(0, 5).map(o => (
                <div key={o.id} className="flex items-center justify-between p-2 rounded-sm cursor-pointer hover:bg-red-50" onClick={() => navigate(`/opportunities?id=${o.id}`)}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: '#2C1A0E' }}>{o.name}</p>
                    <p className="text-[10px]" style={{ color: '#8B7355' }}>{o.deadline ? `Due: ${new Date(o.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : o.category}</p>
                  </div>
                  {o.estimated_revenue > 0 && <span className="text-[10px] font-medium flex-shrink-0" style={{ color: '#B8860B' }}>£{o.estimated_revenue.toLocaleString()}</span>}
                </div>
              ))}
              {(data.red_priority?.length || 0) === 0 && <p className="text-xs text-center py-4" style={{ color: '#8B7355' }}>No immediate actions</p>}
            </div>
            {(data.orange_priority?.length || 0) > 0 && (
              <>
                <h3 className="text-[10px] uppercase tracking-wider font-semibold mt-5 mb-3" style={{ color: '#F59E0B' }}>🟠 Plan This Month ({data.orange_priority.length})</h3>
                <div className="flex flex-col gap-2">
                  {data.orange_priority.slice(0, 5).map(o => (
                    <div key={o.id} className="flex items-center justify-between p-2 rounded-sm cursor-pointer hover:bg-amber-50" onClick={() => navigate(`/opportunities?id=${o.id}`)}>
                      <p className="text-xs truncate" style={{ color: '#2C1A0E' }}>{o.name}</p>
                      <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: '#8B7355' }} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Closing Soon & High Value */}
          <div className="bg-white p-5 border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
            <h3 className="text-[10px] uppercase tracking-wider font-semibold mb-4" style={{ color: '#B8860B' }}>Closing Soon</h3>
            <div className="flex flex-col gap-2 mb-5">
              {(data.closing_soon || []).slice(0, 4).map(o => (
                <div key={o.id} className="flex items-center justify-between p-2 rounded-sm border-l-2 cursor-pointer hover:bg-amber-50" style={{ borderColor: '#DC2626' }} onClick={() => navigate(`/opportunities?id=${o.id}`)}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: '#2C1A0E' }}>{o.name}</p>
                    <p className="text-[10px]" style={{ color: '#DC2626' }}>Deadline: {new Date(o.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                  </div>
                </div>
              ))}
              {(data.closing_soon?.length || 0) === 0 && <p className="text-xs py-2" style={{ color: '#8B7355' }}>None closing this week</p>}
            </div>
            <h3 className="text-[10px] uppercase tracking-wider font-semibold mb-3" style={{ color: '#2C1A0E' }}>High-Value Opportunities</h3>
            <div className="flex flex-col gap-2">
              {(data.high_value || []).slice(0, 4).map(o => (
                <div key={o.id} className="flex items-center justify-between p-2 rounded-sm cursor-pointer hover:bg-amber-50" onClick={() => navigate(`/opportunities?id=${o.id}`)}>
                  <p className="text-xs truncate" style={{ color: '#2C1A0E' }}>{o.name}</p>
                  <span className="text-xs font-semibold flex-shrink-0" style={{ color: '#B8860B' }}>£{(o.estimated_revenue || 0).toLocaleString()}</span>
                </div>
              ))}
              {(data.high_value?.length || 0) === 0 && <p className="text-xs py-2" style={{ color: '#8B7355' }}>No high-value opportunities yet</p>}
            </div>
          </div>

          {/* By Business & Category */}
          <div className="bg-white p-5 border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
            <h3 className="text-[10px] uppercase tracking-wider font-semibold mb-4" style={{ color: '#2C1A0E' }}>By Business</h3>
            <BySorted data={data.by_business} limit={6} />
            <h3 className="text-[10px] uppercase tracking-wider font-semibold mb-3 mt-5" style={{ color: '#2C1A0E' }}>Top Categories</h3>
            <ByCategorySorted data={data.by_category} limit={5} />
          </div>
        </div>

        {/* Revenue Roadmap */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-light" style={{ color: '#2C1A0E' }}>Revenue Roadmap</h2>
              <p className="text-[10px]" style={{ color: '#8B7355' }}>Quarterly targets & pathway to scale — built from the ground up</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[
              { name: 'Spaza Global', target: 150000, currency: '$', streams: ['Marketplace commissions', 'Spaza Luxe B2B products', 'Corporate gifting', 'Trade missions', 'Supplier fees'], phase: '$15k/mo target — marketplace + Luxe product line', color: '#C2410C' },
              { name: 'MAPS', target: 250000, currency: '£', streams: ['Trade expedition packages', 'Buyer introductions', 'Supplier access fees', 'Consulting', 'Government programmes'], phase: 'Pilot running — first bookings targeted', color: '#0D9488' },
              { name: 'Spaza Luxe Escapes', target: 250000, currency: '£', streams: ['Luxury travel packages', 'Corporate retreats', 'Cultural experiences', 'Hotel commissions', 'Curated itineraries'], phase: 'Building product & destination partnerships', color: '#7C3AED' },
              { name: 'Star Talent Group', target: 250000, currency: '£', streams: ['Artist bookings', 'Event production', 'Publishing royalties', 'Corporate entertainment', 'Tour management'], phase: 'Rebuilding roster & corporate pipeline', color: '#1E3A5F' },
              { name: 'Star Global Remit', target: 250000, currency: '£', streams: ['Transfer fees', 'Currency margins', 'B2B payroll', 'Corporate accounts', 'Partnership referrals'], phase: 'Platform build & FCA/regulatory prep', color: '#B91C1C' },
              { name: 'KiddiPlay', target: 100000, currency: '£', streams: ['Venue partner commissions', 'Brand sponsorship (LEGO/Disney/Bluey)', 'Family travel referrals', 'Experience provider listings', 'Seasonal campaign fees'], phase: 'Every venue wants families — KiddiPlay delivers them', color: '#EC4899' },
              { name: 'Maluti Holdings', target: 100000, currency: '£', streams: ['Advisory fees', 'Speaking engagements', 'Consulting retainers', 'Portfolio management', 'Brand licensing'], phase: 'The architect — oversees the portfolio', color: '#B8860B' },
            ].map(({ name, target, currency, streams, phase, color }) => {
              const bizOpps = (data.by_business || {})[name] || 0;
              const bizPipeline = opportunities?.filter(o => o.relevant_businesses?.includes(name)).reduce((s, o) => s + (o.estimated_revenue || 0), 0) || 0;
              const quarterTarget = target / 4;
              const pipelinePct = Math.min(100, Math.round((bizPipeline / quarterTarget) * 100));
              
              return (
                <div key={name} className="bg-white p-4 border rounded-sm" style={{ borderColor: '#E8E0D4', borderLeftWidth: '3px', borderLeftColor: color }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold" style={{ color: '#2C1A0E' }}>{name}</h3>
                    <span className="text-[10px] font-medium" style={{ color }}>{currency}{(target/1000).toFixed(0)}k/yr</span>
                  </div>
                  <p className="text-[10px] italic mb-3" style={{ color: '#8B7355' }}>{phase}</p>
                  
                  {/* Quarterly target bar */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] uppercase tracking-wider" style={{ color: '#8B7355' }}>Q Pipeline vs {currency}{(quarterTarget/1000).toFixed(0)}k target</span>
                      <span className="text-[10px] font-semibold" style={{ color: pipelinePct >= 50 ? '#059669' : pipelinePct >= 25 ? '#D97706' : '#DC2626' }}>{pipelinePct}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#F5F0E8' }}>
                      <div className="h-2 rounded-full transition-all" style={{ width: `${pipelinePct}%`, backgroundColor: color }} />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[9px]" style={{ color: '#8B7355' }}>{currency}{bizPipeline.toLocaleString()} pipeline</span>
                      <span className="text-[9px]" style={{ color: '#8B7355' }}>{bizOpps} opps</span>
                    </div>
                  </div>
                  
                  {/* Revenue streams */}
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#F5F0E8' }}>
                    <p className="text-[9px] uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Revenue Streams</p>
                    <div className="flex flex-wrap gap-1">
                      {streams.map(s => <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${color}10`, color }}>{s}</span>)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/opportunities" className="inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-wider font-medium border rounded-sm no-underline hover:opacity-80" style={{ borderColor: '#D4AF37', color: '#B8860B' }} data-testid="view-all-opps">
            <Briefcase className="w-3 h-3" /> View All Opportunities
          </Link>
          <Link to="/annual-planner" className="inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-wider font-medium border rounded-sm no-underline hover:opacity-80" style={{ borderColor: '#D4AF37', color: '#B8860B' }}>
            <Calendar className="w-3 h-3" /> Annual Planner
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BIDashboard;
